import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { GoVerified } from "react-icons/go";
import { HiOutlineHome } from 'react-icons/hi2';
import { IsMobileEnv } from '../../configurations/initUrls';
import useAuth from '../../configurations/auth/publicUsers/useAuth';
import QRScanner from '../../configurations/native/QRScanner';
import { MdOutlineFormatListBulleted, MdClose } from 'react-icons/md';
import { CgUserList } from 'react-icons/cg';
import { VscCoverage } from 'react-icons/vsc';
import { BsThreeDots } from 'react-icons/bs';
import { GrFavorite } from 'react-icons/gr';
import { HiChevronRight } from 'react-icons/hi2';

const FabToggleMobile = () => {
    const { authentication } = useAuth();
    const location = useLocation();
    const [checkOpen, setCheckOpen] = useState(false);
    const containerRef = useRef(null);

    // bahar tap/click karne par popup band karo
    useEffect(() => {
        if (!checkOpen) return;

        const handleOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setCheckOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutside);
        document.addEventListener('touchstart', handleOutside);
        return () => {
            document.removeEventListener('mousedown', handleOutside);
            document.removeEventListener('touchstart', handleOutside);
        };
    }, [checkOpen]);

    // route badalte hi popup band ho jaye
    useEffect(() => {
        setCheckOpen(false);
    }, [location.pathname]);

    const items = [
        { to: '/', icon: <HiOutlineHome size={20} />, label: 'Home', color: 'text-indigo-600' },
        ...(IsMobileEnv && authentication.isLogin
            ? [
                { to: '/myregister', icon: <CgUserList size={20} />, label: 'Registered', color: 'text-purple-600' },
                { to: '/myconfirmed', icon: <VscCoverage size={20} />, label: 'Confirmed', color: 'text-teal-600' },
            ]
            : []),
        { to: '/favorite', icon: <GrFavorite className="text-red-500" size={20} />, label: 'Doctors', color: 'text-rose-600' },
    ];

    // Check ke andar khulne wale 2 options
    const checkSubItems = [
        {
            to: '/checkregister',
            icon: <MdOutlineFormatListBulleted size={18} />,
            label: 'Check Registered',
            desc: 'Track your registration',
            color: 'text-green-600',
            iconBg: 'bg-green-50',
            activeRing: 'ring-green-200',
        },
        {
            to: '/checkconfirmed',
            icon: <GoVerified size={18} />,
            label: 'Check Confirmed',
            desc: 'View confirmed OPD',
            color: 'text-blue-600',
            iconBg: 'bg-blue-50',
            activeRing: 'ring-blue-200',
        },
    ];

    // agar sub-route active hai to Check bhi highlighted dikhe
    const checkActive = checkSubItems.some((s) => s.to === location.pathname);

    return (
        <>
            {/* Dim backdrop — popup khulne par piche ka content halka dark + blur */}
            <div
                onClick={() => setCheckOpen(false)}
                className={`
                    fixed inset-0 z-40 bg-black/20 backdrop-blur-[2px]
                    transition-opacity duration-300
                    ${checkOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
                `}
                aria-hidden="true"
            />

            <div ref={containerRef} className="w-full max-w-md px-1.5 fixed bottom-4 left-1/2 -translate-x-1/2 z-50">

                {/* ===== Popup menu (hamesha render, transition se show/hide) ===== */}
                <div
                    className={`
                        absolute bottom-full right-1.5 mb-3
                        w-[min(280px,calc(100vw-24px))]
                        origin-bottom-right
                        transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]
                        ${checkOpen
                            ? 'opacity-100 translate-y-0 scale-100'
                            : 'opacity-0 translate-y-3 scale-90 pointer-events-none'}
                    `}
                >
                    <div className="rounded-3xl bg-white/95 backdrop-blur-xl border border-gray-200/70 shadow-2xl shadow-black/10 overflow-hidden">
                        {/* Chhota header */}
                        <div className="flex items-center justify-between px-4 pt-3 pb-1">
                            <span className="text-[11px] uppercase tracking-widest font-bold text-slate-400">
                                Quick Check
                            </span>
                            <button
                                type="button"
                                onClick={() => setCheckOpen(false)}
                                className="w-6 h-6 flex items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 active:scale-90 transition"
                                aria-label="Close menu"
                            >
                                <MdClose size={14} />
                            </button>
                        </div>

                        <div className="p-2 pt-1 flex flex-col gap-1">
                            {checkSubItems.map((sub, i) => {
                                const subActive = location.pathname === sub.to;

                                return (
                                    <Link
                                        key={sub.to}
                                        to={sub.to}
                                        onClick={() => setCheckOpen(false)}
                                        style={{ transitionDelay: checkOpen ? `${80 + i * 60}ms` : '0ms' }}
                                        className={`
                                            group flex items-center gap-3 px-3 py-2.5 rounded-2xl
                                            transition-all duration-300
                                            ${checkOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}
                                            ${subActive
                                                ? `bg-slate-50 ring-1 ${sub.activeRing}`
                                                : 'hover:bg-slate-50 active:bg-slate-100'}
                                        `}
                                    >
                                        {/* Tonal icon bubble */}
                                        <span className={`w-9 h-9 shrink-0 rounded-xl ${sub.iconBg} ${sub.color} flex items-center justify-center`}>
                                            {sub.icon}
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className={`block text-[13px] font-semibold truncate ${subActive ? sub.color : 'text-slate-800'}`}>
                                                {sub.label}
                                            </span>
                                            <span className="block text-[11px] text-slate-400 truncate">
                                                {sub.desc}
                                            </span>
                                        </span>
                                        <HiChevronRight
                                            size={16}
                                            className="shrink-0 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-0.5 transition-all"
                                        />
                                    </Link>
                                );
                            })}
                        </div>
                    </div>

                    {/* Neeche ki taraf chhota pointer/arrow — three dots button ki taraf */}
                    <div className="absolute -bottom-[7px] right-4 w-3.5 h-3.5 rotate-45 bg-white/95 border-b border-r border-gray-200/70" />
                </div>

                {/* ===== Bottom nav bar (same as pehle) ===== */}
                <div className="relative w-full flex items-center justify-between rounded-[32px] bg-white/40 backdrop-blur-md border border-gray-700/20 shadow-xl p-1">

                    {items.map((item) => {
                        const active = location.pathname === item.to;

                        return (
                            <Link key={item.to} to={item.to} className="flex-1 flex justify-center min-w-0">
                                <div
                                    className={`
                                        flex flex-col items-center justify-center
                                        w-full min-w-0 px-2 py-2 rounded-[28px]
                                        transition-all duration-300
                                        ${active ? 'bg-black/10 scale-105' : 'hover:bg-black/5'}
                                    `}
                                >
                                    <div className={active ? item.color : "text-slate-900"}>
                                        {item.icon}
                                    </div>
                                    <span className={`text-[10px] leading-tight font-medium mt-1 truncate max-w-full ${active ? item.color : 'text-slate-800'}`}>
                                        {item.label}
                                    </span>
                                </div>
                            </Link>
                        );
                    })}
                    <QRScanner />

                    <div className="shrink-0 flex justify-center">
                        <button
                            type="button"
                            onClick={() => setCheckOpen((p) => !p)}
                            className={`
                                flex items-center justify-center
                                w-10 h-10 rounded-full
                                transition-all duration-300
                                ${checkOpen || checkActive ? 'bg-black/10' : 'hover:bg-black/5'}
                            `}
                            aria-label="More"
                            aria-expanded={checkOpen}
                        >
                            {/* Icon rotate ho kar cross-feel deta hai jab open ho */}
                            <div
                                className={`
                                    transition-transform duration-300
                                    ${checkOpen ? 'rotate-90 text-orange-600' : checkActive ? 'text-orange-600' : 'text-slate-900'}
                                `}
                            >
                                <BsThreeDots size={22} />
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default FabToggleMobile;